import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Image, Copy, Check, Trash2, ImagePlus, Loader } from 'lucide-react';
import { imagesAPI } from '../api/images';
import '../styles/ImagesPage.css';

const CATEGORIES = [
  { value: 'all',     label: 'All Images' },
  { value: 'logo',    label: 'Logos'      },
  { value: 'icon',    label: 'Icons'      },
  { value: 'header',  label: 'Headers'    },
  { value: 'footer',  label: 'Footers'    },
  { value: 'product', label: 'Products'   },
  { value: 'social',  label: 'Social'     },
  { value: 'other',   label: 'Other'      },
];

const ImagesPage = () => {
  const [images, setImages]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [uploading, setUploading]         = useState(false);
  const [dragActive, setDragActive]       = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [copiedUrl, setCopiedUrl]         = useState(null);

  const loadImages = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await imagesAPI.getAll(params);
      setImages(response.images || []);
    } catch (error) {
      console.error('Error loading images:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => { loadImages(); }, [loadImages]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files?.length > 0) await uploadFiles(Array.from(files));
  };

  const handleFileInput = async (e) => {
    const files = e.target.files;
    if (files?.length > 0) await uploadFiles(Array.from(files));
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        await imagesAPI.upload(file, selectedCategory === 'all' ? 'other' : selectedCategory);
      }
      await loadImages();
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await imagesAPI.delete(imageId);
      await loadImages();
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const copyImageUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  return (
    <div className="images-page">

      {/* ── Header ────────────────────────────────────────────── */}
      <div className="images-page-header">
        <div className="images-page-header-icon">
          <Image size={18} color="#00D9FF" />
        </div>
        <div>
          <h1 className="images-page-title">Image Library</h1>
          <p className="images-page-subtitle">Upload and manage reusable images for your email templates</p>
        </div>
      </div>

      {/* ── Category tabs ─────────────────────────────────────── */}
      <div className="img-cat-tabs">
        {CATEGORIES.map(cat => (
          <button
            key={cat.value}
            className={`img-cat-tab${selectedCategory === cat.value ? ' active' : ''}`}
            onClick={() => setSelectedCategory(cat.value)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* ── Upload area ───────────────────────────────────────── */}
      <div
        className={`img-upload-area${dragActive ? ' drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="img-upload-icon">
          <Upload size={28} color={dragActive ? '#00D9FF' : 'rgba(255,255,255,0.3)'} strokeWidth={1.5} />
        </div>
        <p className="img-upload-text">
          Drop images here, or{' '}
          <label className="img-upload-link">
            browse to upload
            <input type="file" multiple accept="image/*" onChange={handleFileInput} style={{ display: 'none' }} />
          </label>
        </p>
        <p className="img-upload-hint">PNG, JPG, GIF · up to 10 MB</p>
      </div>

      {/* ── Loading ───────────────────────────────────────────── */}
      {loading && (
        <div className="img-loading">
          <Loader size={20} color="rgba(255,255,255,0.3)" className="img-spinner" />
          <span>Loading images…</span>
        </div>
      )}

      {/* ── Grid ─────────────────────────────────────────────── */}
      {!loading && images.length > 0 && (
        <div className="img-grid">
          {images.map(image => (
            <div key={image._id} className="img-card">
              <div className="img-preview">
                <img src={image.url} alt={image.originalName} />
                <div className="img-overlay">
                  <button
                    className="img-overlay-btn img-copy-btn"
                    onClick={() => copyImageUrl(image.url)}
                    title="Copy URL"
                  >
                    {copiedUrl === image.url
                      ? <><Check size={13} /> Copied</>
                      : <><Copy size={13} /> Copy URL</>
                    }
                  </button>
                  <button
                    className="img-overlay-btn img-delete-btn"
                    onClick={() => handleDelete(image._id)}
                    title="Delete"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
              <div className="img-info">
                <p className="img-name" title={image.originalName}>{image.originalName}</p>
                <p className="img-meta">
                  {[
                    image.width && image.height ? `${image.width} × ${image.height}` : null,
                    image.size ? `${Math.round(image.size / 1024)}KB` : null,
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ───────────────────────────────────────── */}
      {!loading && images.length === 0 && (
        <div className="img-empty">
          <div className="img-empty-icon">
            <ImagePlus size={36} strokeWidth={1} color="rgba(255,255,255,0.15)" />
          </div>
          <h3>No images yet</h3>
          <p>Upload your first image to get started</p>
        </div>
      )}

      {/* ── Upload overlay ────────────────────────────────────── */}
      {uploading && (
        <div className="img-uploading-overlay">
          <div className="img-uploading-modal">
            <Loader size={22} color="#00D9FF" className="img-spinner" />
            <p>Uploading…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagesPage;
